import { useCallback } from 'react';
import uuid from 'uuid';
import { trackBulkUserAction } from 'talent_matching/api';
import { useUserContext } from 'talent_matching/contexts/user_context';
import type { TalentMatchingSource } from 'talent_matching/lib/tracker';

/**
 * Returns a tracker for a bulk action. Generates one `bulk_action_uuid` per
 * invocation and hits the backend fan-out endpoint, which emits one analytics
 * event per application. Call it once, on success, from a bulk modal. Returns a
 * promise that settles when the request completes and never rejects, so callers
 * may optionally await it before navigating (e.g. a page reload). See
 * GREEN-69176.
 */
export function useTrackBulkAction(
  source: TalentMatchingSource,
  appReviewSessionId?: number | null
) {
  const { hiringPlanId } = useUserContext();

  return useCallback(
    (
      trackedAction: string,
      applicationIds: number[],
      // Per-application fields (e.g. target_hiring_plan_id, is_same_job) promoted
      // to top-level on each event to match the single-action shape. The backend
      // allowlists which keys are promoted; use `metadata` for everything else.
      entityFields?: Record<string, unknown>,
      metadata?: Record<string, unknown>
    ): Promise<void> =>
      trackBulkUserAction({
        source,
        tracked_action: trackedAction,
        application_ids: applicationIds,
        hiring_plan_id: hiringPlanId,
        bulk_action_uuid: uuid.v4(),
        app_review_session_id: appReviewSessionId,
        entity_fields: entityFields,
        metadata,
      }),
    [source, hiringPlanId, appReviewSessionId]
  );
}
